import { create } from 'zustand';
import type { Language, Currency, AppSettings } from '@/types';

export type WidgetType = 'currency' | 'weather' | 'air' | 'calendar' | 'holiday' | 'word';

// Note: 'air' is not in default enabled widgets - it's disabled by default
const DEFAULT_WIDGET_ORDER: WidgetType[] = ['currency', 'weather', 'air', 'calendar', 'holiday', 'word'];
const DEFAULT_ENABLED_WIDGETS: WidgetType[] = ['currency', 'weather', 'calendar', 'holiday', 'word'];

interface AppStore extends AppSettings {
  favorites: Set<string>;
  enabledWidgets: WidgetType[];
  widgetOrder: WidgetType[];

  // Settings actions
  setLanguage: (language: Language) => void;
  setCurrency: (currency: Currency) => void;
  setEnableFavorites: (enable: boolean) => void;

  // Widgets actions
  toggleWidget: (widget: WidgetType) => void;
  isWidgetEnabled: (widget: WidgetType) => boolean;
  reorderWidgets: (newOrder: WidgetType[]) => void;
  getOrderedEnabledWidgets: () => WidgetType[];

  // Favorites actions
  addFavorite: (id: string) => void;
  removeFavorite: (id: string) => void;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  getFavorites: () => string[];
  clearFavorites: () => void;
}

// Ensure widgetOrder contains all widget types (for when new widgets are added)
const ensureAllWidgetsInOrder = (order: WidgetType[]): WidgetType[] => {
  const missingWidgets = DEFAULT_WIDGET_ORDER.filter(w => !order.includes(w));
  return [...order, ...missingWidgets];
};

// Helper functions for localStorage
const loadState = (): { language: Language; currency: Currency; enableFavorites: boolean; favorites: string[]; enabledWidgets: WidgetType[]; widgetOrder: WidgetType[] } => {
  try {
    const stored = localStorage.getItem('umland-store');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure all widgets are in the order (handles new widgets added after user's first visit)
      const widgetOrder = ensureAllWidgetsInOrder(parsed.widgetOrder || DEFAULT_WIDGET_ORDER);
      return {
        ...parsed,
        widgetOrder,
      };
    }
  } catch (e) {
    console.error('Failed to load store:', e);
  }
  return {
    language: 'en',
    currency: 'EUR',
    enableFavorites: true,
    favorites: [],
    enabledWidgets: DEFAULT_ENABLED_WIDGETS,
    widgetOrder: DEFAULT_WIDGET_ORDER,
  };
};

const saveState = (state: Partial<AppStore>) => {
  try {
    const toSave = {
      language: state.language,
      currency: state.currency,
      enableFavorites: state.enableFavorites,
      favorites: Array.from(state.favorites || new Set()),
      enabledWidgets: state.enabledWidgets || DEFAULT_ENABLED_WIDGETS,
      widgetOrder: state.widgetOrder || DEFAULT_WIDGET_ORDER,
    };
    localStorage.setItem('umland-store', JSON.stringify(toSave));
  } catch (e) {
    console.error('Failed to save store:', e);
  }
};

const initialState = loadState();

export const useAppStore = create<AppStore>((set, get) => ({
  language: (initialState.language as Language) || 'en',
  currency: (initialState.currency as Currency) || 'ISK',
  enableFavorites: initialState.enableFavorites !== false,
  favorites: new Set(initialState.favorites || []),
  enabledWidgets: initialState.enabledWidgets || DEFAULT_ENABLED_WIDGETS,
  widgetOrder: initialState.widgetOrder || DEFAULT_WIDGET_ORDER,

  setLanguage: (language: Language) => {
    set((state) => {
      const newState = { ...state, language };
      saveState(newState);
      return newState;
    });
  },

  setCurrency: (currency: Currency) => {
    set((state) => {
      const newState = { ...state, currency };
      saveState(newState);
      return newState;
    });
  },

  setEnableFavorites: (enable: boolean) => {
    set((state) => {
      const newState = { ...state, enableFavorites: enable };
      saveState(newState);
      return newState;
    });
  },

  toggleWidget: (widget: WidgetType) => {
    set((state) => {
      const enabledWidgets = state.enabledWidgets.includes(widget)
        ? state.enabledWidgets.filter(w => w !== widget)
        : [...state.enabledWidgets, widget];
      const newState = { ...state, enabledWidgets };
      saveState(newState);
      return newState;
    });
  },

  isWidgetEnabled: (widget: WidgetType) => {
    return get().enabledWidgets.includes(widget);
  },

  reorderWidgets: (newOrder: WidgetType[]) => {
    set((state) => {
      const newState = { ...state, widgetOrder: newOrder };
      saveState(newState);
      return newState;
    });
  },

  getOrderedEnabledWidgets: () => {
    const state = get();
    return state.widgetOrder.filter((w) => state.enabledWidgets.includes(w));
  },

  addFavorite: (id: string) => {
    const favorites = new Set(get().favorites);
    favorites.add(id);
    set({ favorites });
    saveState({ ...get(), favorites });
  },

  removeFavorite: (id: string) => {
    const favorites = new Set(get().favorites);
    favorites.delete(id);
    set({ favorites });
    saveState({ ...get(), favorites });
  },

  toggleFavorite: (id: string) => {
    const { isFavorite: isFav, addFavorite, removeFavorite } = get();
    if (isFav(id)) {
      removeFavorite(id);
    } else {
      addFavorite(id);
    }
  },

  isFavorite: (id: string) => {
    return get().favorites.has(id);
  },

  getFavorites: () => {
    return Array.from(get().favorites);
  },

  clearFavorites: () => {
    const newFavorites = new Set<string>();
    set({ favorites: newFavorites });
    saveState({ ...get(), favorites: newFavorites });
  },
}));
